import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  BookOpen,
  ChevronDown,
  KeyRound,
  Menu,
  X,
  Sun,
  Moon,
  LogIn,
  LogOut,
  User,
  UserRound,
  Wallet,
} from "lucide-react";

import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";


const NAV = [
  {
    to: "/",
    label: "Home",
  },
  {
    to: "/services",
    label: "Services",
  },
  {
    to: "/about",
    label: "About Us",
  },
  {
    to: "/contact",
    label: "Contact Us",
  },
];


const ACCOUNT_NAV = [
  {
    to: "/ledger",
    label: "Ledger",
    icon: BookOpen,
  },

  {
    to: "/profile",
    label: "Profile",
    icon: UserRound,
  },

  {
    to: "/change-password",
    label: "Change Password",
    icon: KeyRound,
  },
];


export const Header = () => {

  const {
    theme,
    toggleTheme,
  } = useTheme();

  const {
    user,
    logout,
  } = useAuth();


  const [open, setOpen] =
    useState(false);

  const [
    accountOpen,
    setAccountOpen,
  ] = useState(false);


  const accountMenuRef =
    useRef(null);

  const location =
    useLocation();

  const navigate =
    useNavigate();


  const {
    walletBalance,
    refreshWallet,
  } = useWallet();


  /*
   * Close menus whenever route changes.
   */
  useEffect(() => {
    setOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);


  /*
   * Close account menu when clicking
   * outside or pressing Escape.
   */
  useEffect(() => {

    const closeAccountMenu = (
      event
    ) => {

      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(
          event.target
        )
      ) {
        setAccountOpen(false);
      }
    };


    const closeOnEscape = (
      event
    ) => {

      if (event.key === "Escape") {
        setAccountOpen(false);
      }
    };


    document.addEventListener(
      "mousedown",
      closeAccountMenu
    );

    document.addEventListener(
      "keydown",
      closeOnEscape
    );


    return () => {

      document.removeEventListener(
        "mousedown",
        closeAccountMenu
      );

      document.removeEventListener(
        "keydown",
        closeOnEscape
      );
    };

  }, []);


  /*
   * Refresh wallet after login.
   */
  useEffect(() => {

    if (user) {
      refreshWallet();
    }

  }, [
    user,
    refreshWallet,
  ]);


  /*
   * Login / Logout
   */
  const handleAuthClick = () => {

    if (user) {

      logout();

      toast.success(
        "You have been logged out."
      );

      navigate("/login");

    } else {

      navigate("/login");

    }
  };


  return (
    <header
      className="
        fixed
        top-0
        inset-x-0
        z-50
        h-[65px]
        glass-header
      "
      data-testid="site-header"
    >

      <div
        className="
          mx-auto
          h-full
          max-w-7xl
          pl-1
          pr-4
          sm:pl-2
          sm:pr-6
          lg:pl-2
          lg:pr-8
          flex
          items-center
        "
      >

        {/* Logo */}
        <div
          className="
            flex-1
            flex
            items-center
          "
        >
          <Logo />
        </div>


        {/* User information */}
        <div
          className="
            hidden
            md:flex
            flex-1
            items-center
            justify-center
          "
        >

          {user ? (

            <div
              className="
                glass-card
                rounded-full
                px-6
                py-2
                flex
                items-center
                gap-3
                min-w-[450px]
              "
              data-testid="header-user-info"
            >

              <div
                className="
                  h-8
                  w-8
                  rounded-full
                  btn-gradient
                  flex
                  items-center
                  justify-center
                  text-white
                "
              >
                <User
                  className="h-4 w-4"
                  strokeWidth={2.4}
                />
              </div>


              <div className="leading-tight">

                <div
                  className="
                    text-[11px]
                    uppercase
                    tracking-[0.2em]
                    text-muted-foreground
                  "
                >
                  Welcome
                </div>


                <div
                  className="
                    flex
                    items-center
                    gap-4
                  "
                >

                  <span
                    className="
                      font-display
                      font-semibold
                      text-sm
                    "
                    data-testid="header-user-name"
                  >
                    {user.UserName}
                  </span>


                  <button
                    type="button"
                    onClick={() =>
                      navigate("/wallet")
                    }
                    title="Top up wallet"
                    aria-label={
                      `Wallet balance ₹${walletBalance}. Click to top up.`
                    }
                    data-testid="header-wallet-topup"
                    className="
                      group
                      flex
                      items-center
                      gap-2
                      rounded-full
                      bg-emerald-500/10
                      px-3
                      py-1.5
                      ring-1
                      ring-emerald-500/15
                      transition-all
                      hover:bg-emerald-500/20
                      hover:ring-emerald-500/30
                      hover:shadow-md
                      active:scale-[0.98]
                    "
                  >

                    <Wallet
                      className="
                        h-5
                        w-5
                        text-emerald-600
                        transition-transform
                        group-hover:scale-110
                      "
                      strokeWidth={2.2}
                    />

                    <span
                      className="
                        text-sm
                        font-semibold
                        text-emerald-600
                      "
                    >
                      ₹ {walletBalance}
                    </span>

                  </button>

                </div>
              </div>

            </div>

          ) : null}

        </div>


        {/* Right Navigation */}
        <div
          className="
            flex-1
            flex
            items-center
            justify-end
            gap-2
          "
        >

          {/* Desktop navigation */}
          <nav
            className="
              hidden
              lg:flex
              items-center
              gap-1
              mr-2
            "
            aria-label="Primary"
          >

            {NAV.map((item) => (

              <NavLink
                key={item.label}
                to={item.to}
                end={item.to === "/"}
                data-testid={
                  `nav-link-${item.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`
                }
                className={({
                  isActive,
                }) =>
                  `
                    relative
                    px-3
                    py-2
                    text-sm
                    font-medium
                    rounded-full
                    transition-colors
                    whitespace-nowrap
                    ${
                      isActive
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }
                  `
                }
              >

                {({
                  isActive,
                }) => (
                  <>
                    {item.label}

                    {isActive && (
                      <span
                        className="
                          absolute
                          left-3
                          right-3
                          -bottom-0.5
                          h-[2px]
                          rounded-full
                          btn-gradient
                        "
                      />
                    )}
                  </>
                )}

              </NavLink>

            ))}

          </nav>


          {/* Theme */}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            data-testid="theme-toggle-btn"
            className="
              h-10
              w-10
              rounded-full
              glass-card
              hover-lift
              flex
              items-center
              justify-center
            "
          >

            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}

          </button>


          {/* Account dropdown */}
          {user && (

            <div
              ref={accountMenuRef}
              className="
                relative
                hidden
                sm:block
              "
            >

              <button
                type="button"
                onClick={() =>
                  setAccountOpen(
                    (current) => !current
                  )
                }
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                data-testid="account-menu-toggle"
                className={`
                  inline-flex
                  items-center
                  gap-2
                  h-10
                  px-3.5
                  rounded-full
                  glass-card
                  text-sm
                  font-semibold
                  transition-all
                  ${
                    accountOpen
                      ? "ring-2 ring-primary/25 text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }
                `}
              >

                <UserRound className="h-4 w-4" />

                <span className="hidden xl:inline">
                  My Account
                </span>

                <ChevronDown
                  className={`
                    h-3.5
                    w-3.5
                    transition-transform
                    duration-200
                    ${
                      accountOpen
                        ? "rotate-180"
                        : ""
                    }
                  `}
                />

              </button>


              <div
                role="menu"
                className={`
                  absolute
                  right-0
                  top-full
                  mt-2
                  w-56
                  origin-top-right
                  transition-all
                  duration-200
                  ${
                    accountOpen
                      ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
                  }
                `}
              >

                <div
                  className="
                    glass-card
                    rounded-2xl
                    border
                    border-border/70
                    p-2
                    shadow-2xl
                  "
                >

                  <div
                    className="
                      px-3
                      py-2.5
                      border-b
                      border-border/60
                      mb-1
                    "
                  >

                    <p
                      className="
                        text-[10px]
                        uppercase
                        tracking-[0.18em]
                        text-muted-foreground
                      "
                    >
                      Account
                    </p>

                    <p
                      className="
                        mt-0.5
                        text-sm
                        font-semibold
                        truncate
                      "
                    >
                      {user.UserName || user.name}
                    </p>

                  </div>


                  {ACCOUNT_NAV.map(
                    ({
                      to,
                      label,
                      icon: Icon,
                    }) => (

                      <NavLink
                        key={to}
                        to={to}
                        role="menuitem"
                        className={({
                          isActive,
                        }) =>
                          `
                            flex
                            items-center
                            gap-3
                            px-3
                            py-2.5
                            rounded-xl
                            text-sm
                            transition-colors
                            ${
                              isActive
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground"
                            }
                          `
                        }
                      >

                        <span
                          className="
                            h-8
                            w-8
                            rounded-lg
                            bg-foreground/[0.05]
                            flex
                            items-center
                            justify-center
                          "
                        >
                          <Icon className="h-4 w-4" />
                        </span>

                        {label}

                      </NavLink>

                    )
                  )}


                  <div
                    className="
                      mt-1
                      border-t
                      border-border/60
                      pt-1
                    "
                  >

                    <button
                      type="button"
                      role="menuitem"
                      onClick={
                        handleAuthClick
                      }
                      data-testid="account-logout-btn"
                      className="
                        w-full
                        flex
                        items-center
                        gap-3
                        px-3
                        py-2.5
                        rounded-xl
                        text-sm
                        font-medium
                        text-red-500
                        hover:bg-red-500/10
                        transition-colors
                      "
                    >

                      <span
                        className="
                          h-8
                          w-8
                          rounded-lg
                          bg-red-500/10
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <LogOut className="h-4 w-4" />
                      </span>

                      Logout

                    </button>

                  </div>

                </div>

              </div>

            </div>

          )}


          {/* Login */}
          {!user && (

            <button
              type="button"
              onClick={
                handleAuthClick
              }
              data-testid="login-btn"
              className="
                hidden
                sm:inline-flex
                items-center
                gap-2
                h-10
                px-4
                rounded-full
                btn-gradient
                text-white
                text-sm
                font-semibold
                ripple
              "
            >

              <LogIn className="h-4 w-4" />

              Login

            </button>

          )}


          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() =>
              setOpen(
                (current) => !current
              )
            }
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="
              lg:hidden
              h-10
              w-10
              rounded-full
              glass-card
              flex
              items-center
              justify-center
            "
          >

            {open ? (
              <X className="h-4 w-4" />
            ) : (
              <Menu className="h-4 w-4" />
            )}

          </button>

        </div>

      </div>


      {/* Mobile Menu */}
      <div
        className={`
          lg:hidden
          absolute
          inset-x-0
          top-[100px]
          origin-top
          transition-all
          duration-300
          ${
            open
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-2 pointer-events-none"
          }
        `}
        data-testid="mobile-menu-panel"
      >

        <div
          className="
            mx-4
            sm:mx-6
            mt-2
            glass-card
            rounded-2xl
            p-4
            flex
            flex-col
            gap-1
          "
        >

          {user && (

            <div
              className="
                mb-2
                px-3
                py-3
                rounded-xl
                bg-foreground/[0.04]
              "
            >

              <div
                className="
                  text-[10px]
                  uppercase
                  tracking-[0.2em]
                  text-muted-foreground
                "
              >
                Welcome
              </div>

              <div
                className="
                  flex
                  items-baseline
                  gap-2
                  mt-1
                "
              >

                <span
                  className="
                    font-display
                    font-semibold
                    text-sm
                  "
                >
                  {user.UserName || user.name}
                </span>

                <span
                  className="
                    font-mono
                    text-xs
                    text-muted-foreground
                  "
                >
                  {user.UserID || user.mobile}
                </span>

              </div>


              <button
                type="button"
                onClick={() =>
                  navigate("/wallet")
                }
                data-testid="mobile-wallet-topup"
                className="
                  mt-3
                  w-full
                  flex
                  items-center
                  justify-between
                  rounded-xl
                  bg-emerald-500/10
                  px-3
                  py-2.5
                  text-emerald-600
                  hover:bg-emerald-500/20
                  transition-colors
                "
              >

                <span
                  className="
                    flex
                    items-center
                    gap-2
                    text-sm
                    font-semibold
                  "
                >

                  <Wallet
                    className="h-5 w-5"
                    strokeWidth={2.2}
                  />

                  ₹ {walletBalance}

                </span>

                <span
                  className="
                    text-xs
                    font-bold
                    uppercase
                    tracking-wider
                  "
                >
                  Top Up
                </span>

              </button>

            </div>

          )}


          {/* Mobile Navigation */}
          {NAV.map((item) => (

            <NavLink
              key={item.label}
              to={item.to}
              end={item.to === "/"}
              data-testid={
                `mobile-nav-link-${item.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`
              }
              className={({
                isActive,
              }) =>
                `
                  px-3
                  py-2
                  rounded-xl
                  text-sm
                  ${
                    isActive
                      ? "bg-foreground/[0.06] text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]"
                  }
                `
              }
            >

              {item.label}

            </NavLink>

          ))}


          {/* Mobile Account */}
          {user && (

            <div
              className="
                my-2
                border-t
                border-border/70
                pt-2
              "
            >

              <p
                className="
                  px-3
                  pb-1.5
                  text-[10px]
                  uppercase
                  tracking-[0.2em]
                  text-muted-foreground
                "
              >
                My Account
              </p>


              {ACCOUNT_NAV.map(
                ({
                  to,
                  label,
                  icon: Icon,
                }) => (

                  <NavLink
                    key={to}
                    to={to}
                    className={({
                      isActive,
                    }) =>
                      `
                        flex
                        items-center
                        gap-3
                        px-3
                        py-2.5
                        rounded-xl
                        text-sm
                        ${
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]"
                        }
                      `
                    }
                  >

                    <Icon className="h-4 w-4" />

                    {label}

                  </NavLink>

                )
              )}


              <button
                type="button"
                onClick={
                  handleAuthClick
                }
                data-testid="mobile-account-logout-btn"
                className="
                  mt-1
                  w-full
                  flex
                  items-center
                  gap-3
                  px-3
                  py-2.5
                  rounded-xl
                  text-sm
                  font-medium
                  text-red-500
                  hover:bg-red-500/10
                  transition-colors
                "
              >

                <LogOut className="h-4 w-4" />

                Logout

              </button>

            </div>

          )}


          {!user && (

            <button
              type="button"
              onClick={
                handleAuthClick
              }
              data-testid="mobile-login-btn"
              className="
                mt-2
                inline-flex
                items-center
                justify-center
                gap-2
                h-10
                px-4
                rounded-full
                btn-gradient
                text-white
                text-sm
                font-semibold
              "
            >

              <LogIn className="h-4 w-4" />

              Login

            </button>

          )}

        </div>

      </div>

    </header>
  );
};


export default Header;